import { CameraMode } from '../../../defines';
import { BaseTool } from '../../BaseTool';
import type { SubToolInputKind } from '../../SubTool';

/**
 * The members `SurfaceAlignHosting` drives on `this`: the alignment handlers
 * from `SurfaceToolBase` and `muteInput` from `SubToolHosting`. The mixin casts
 * `this` to this shape internally so it can be applied over ANY BaseTool
 * subclass without erasing the base's other members (a structural constraint on
 * `TBase` would do that). Both surface hosts satisfy it at the call site.
 */
interface SurfaceAlignHost {
  readonly viewer: { scene: { cameraMode: number } };
  handleAlignmentMouseDown(event: MouseEvent): boolean;
  handleAlignmentMouseMove(event: MouseEvent): boolean;
  handleAlignmentMouseUp(event: MouseEvent): boolean;
  handleAlignmentKeyDown(event: KeyboardEvent): boolean;
  isCameraAlignmentActive(): boolean;
  muteInput(kind: SubToolInputKind, mute: boolean): void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BaseToolCtor = abstract new (...args: any[]) => BaseTool;

/**
 * Mixin that adds the Ctrl-gated camera-alignment intercept behavior shared by
 * the surface hosts. Apply it OVER `SubToolHosting(SurfaceToolBase)` so the
 * `interceptXxx` hooks it overrides are the ones `SubToolHosting` calls before
 * routing input to the subtool.
 *
 * Camera alignment only STARTS while Ctrl is held on mousedown (so a plain
 * click draws/places instead of rotating the view); once a gesture is in
 * progress it runs to completion regardless of the modifier. While a gesture is
 * armed or active the subtool's pointer input is muted so a draw click can't
 * fire mid-align.
 */
export function SurfaceAlignHosting<TBase extends BaseToolCtor>(Base: TBase) {
  abstract class SurfaceAligned extends Base {
    private alignmentArmed = false;

    /** Access the surface-align members the concrete chain provides. */
    private get align(): SurfaceAlignHost {
      return this as unknown as SurfaceAlignHost;
    }

    protected interceptMouseDown(event: MouseEvent): boolean {
      const a = this.align;
      this.alignmentArmed = event.ctrlKey;
      if ((this.alignmentArmed || a.isCameraAlignmentActive()) && a.handleAlignmentMouseDown(event)) {
        a.muteInput('pointer', true);
        return true;
      }
      if (this.alignmentArmed && a.viewer.scene.cameraMode === CameraMode.ORTHOGRAPHIC) {
        // First Ctrl+click: SurfaceToolBase only records the position (it starts
        // the gesture on mouseup). Mute draw input; don't route a draw mousedown.
        a.muteInput('pointer', true);
        return true;
      }
      a.muteInput('pointer', a.isCameraAlignmentActive());
      return false;
    }

    protected interceptMouseMove(event: MouseEvent): boolean {
      const a = this.align;
      return a.isCameraAlignmentActive() && a.handleAlignmentMouseMove(event);
    }

    protected interceptMouseUp(event: MouseEvent): boolean {
      const a = this.align;
      if ((this.alignmentArmed || a.isCameraAlignmentActive()) && a.handleAlignmentMouseUp(event)) {
        a.muteInput('pointer', a.isCameraAlignmentActive());
        return true;
      }
      this.alignmentArmed = false;
      a.muteInput('pointer', a.isCameraAlignmentActive());
      return false;
    }

    protected interceptKeyDown(event: KeyboardEvent): boolean {
      return this.align.handleAlignmentKeyDown(event);
    }

    // The mixin can't override onClick on the SubToolHosting base structurally,
    // so concrete hosts keep the small `onClick` guard that drops clicks while
    // alignment is active.
  }

  return SurfaceAligned;
}
